import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import './onboardborrower.css';

const OnboardBorrower = () => {
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    assets: [],
    connectedSystems: []
  });

  // Load existing data from cookies
  useEffect(() => {
    const savedData = getCookie('demo-borrower-data');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(parsed);
        if (parsed.assets && parsed.assets.length > 0) {
          setCurrentStep(3); // Skip to asset details if assets exist
        }
      } catch (e) {
        console.error('Error parsing saved data:', e);
        // Clear invalid cookie data
        document.cookie = 'demo-borrower-data=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }
    }
  }, []);

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      try {
        const cookieValue = parts.pop().split(';').shift();
        return decodeURIComponent(cookieValue);
      } catch (e) {
        console.error('Error decoding cookie:', e);
        return null;
      }
    }
    return null;
  };

  const setCookie = (name, value, days = 7) => {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; expires=${expires}; path=/`;
  };

  const handleConnectSystem = (systemId) => {
    // Only allow one system to be selected at a time
    const updatedSystems = [{ id: systemId, connected: true }];
    
    const newFormData = { ...formData, connectedSystems: updatedSystems };
    setFormData(newFormData);
    setCookie('demo-borrower-data', newFormData);
  };

  const handleAddAsset = (asset) => {
    const newAssets = [...formData.assets, { ...asset, id: Date.now() }];
    const newFormData = { ...formData, assets: newAssets };
    setFormData(newFormData);
    setCookie('demo-borrower-data', newFormData);
  };

  const handleManualAsset = (assetData) => {
    const asset = {
      type: assetData.type,
      name: assetData.name,
      description: assetData.description,
      value: parseFloat(assetData.value),
      location: assetData.location,
      condition: assetData.condition,
      source: 'manual'
    };
    handleAddAsset(asset);
  };

  const handleComplete = () => {
    // Generate loan offers based on assets
    const loanOffers = generateLoanOffers(formData.assets);
    setCookie('demo-loans', loanOffers);
    history.push('/jo-xyz-demo/borrower-dashboard');
  };

  const generateLoanOffers = (assets) => {
    const totalValue = assets.reduce((sum, asset) => sum + asset.value, 0);
    const ltv = 0.65; // 65% LTV
    const loanAmount = totalValue * ltv;
    
    return [
      {
        id: 'offer-1',
        amount: loanAmount,
        interestRate: 6.5,
        term: '24 months',
        ltv: ltv,
        collateral: assets,
        status: 'available'
      }
    ];
  };

  const systems = [
    {
      id: 'netsuite',
      name: 'NetSuite ERP',
      description: 'Connect your NetSuite system to automatically sync inventory and asset data',
      icon: '🏢',
      connected: formData.connectedSystems.some(s => s.id === 'netsuite' && s.connected)
    },
    {
      id: 'quickbooks',
      name: 'QuickBooks',
      description: 'Sync your QuickBooks accounting data for comprehensive asset tracking',
      icon: '📊',
      connected: formData.connectedSystems.some(s => s.id === 'quickbooks' && s.connected)
    },
    {
      id: 'manual',
      name: 'Manual Entry',
      description: 'Add your assets manually with detailed information',
      icon: '✏️',
      connected: formData.connectedSystems.some(s => s.id === 'manual' && s.connected)
    }
  ];

  const assetTypes = [
    { type: 'commercial-property', name: 'Commercial Property', icon: '🏢' },
    { type: 'trucks', name: 'Trucks & Vehicles', icon: '🚛' },
    { type: 'inventory', name: 'Inventory', icon: '📦' },
    { type: 'equipment', name: 'Equipment', icon: '⚙️' }
  ];

  const renderStep1 = () => (
    <div className="jo-xyz-demo-onboard-step">
      <h2>Connect Your Systems</h2>
      <p>Choose how you'd like to connect your asset data. Select one system to proceed.</p>

      <div className="jo-xyz-demo-systems-grid">
        {systems.map((system) => (
          <div
            key={system.id}
            className={`jo-xyz-demo-system-card ${system.connected ? 'jo-xyz-demo-connected' : ''}`}
            onClick={() => handleConnectSystem(system.id)}
            style={{ cursor: 'pointer' }}
          >
            <div className="jo-xyz-demo-system-card__icon">{system.icon}</div>
            <h3>{system.name}</h3>
            <p>{system.description}</p>
            <button
              className={`jo-xyz-demo-btn ${system.connected ? 'jo-xyz-demo-btn--primary' : 'jo-xyz-demo-btn--secondary'}`}
              onClick={(e) => {
                e.stopPropagation(); // Prevent double-click
                handleConnectSystem(system.id);
              }}
            >
              {system.connected ? 'Selected' : 'Select'}
            </button>
          </div>
        ))}
      </div>

      <div className="jo-xyz-demo-step-actions">
        <button
          className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
          onClick={() => setCurrentStep(2)}
          disabled={formData.connectedSystems.length === 0}
        >
          Continue to Assets
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="jo-xyz-demo-onboard-step">
      <h2>Select Asset Types</h2>
      <p>Choose the types of assets you'd like to add to your portfolio.</p>

      <div className="jo-xyz-demo-asset-types-grid">
        {assetTypes.map((assetType) => (
          <div key={assetType.type} className="jo-xyz-demo-asset-type-card">
            <div className="jo-xyz-demo-asset-type-card__icon">{assetType.icon}</div>
            <h3>{assetType.name}</h3>
            <button
              className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
              onClick={() => setCurrentStep(3)}
            >
              Add {assetType.name}
            </button>
          </div>
        ))}
      </div>

      <div className="jo-xyz-demo-step-actions">
        <button
          className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary"
          onClick={() => setCurrentStep(1)}
        >
          Back
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="jo-xyz-demo-onboard-step">
      <h2>Asset Details</h2>
      <p>Add detailed information about your assets.</p>

      {formData.connectedSystems.some(s => s.id === 'netsuite' && s.connected) && (
        <div className="jo-xyz-demo-connected-assets">
          <h3>Assets from NetSuite</h3>
          <div className="jo-xyz-demo-asset-list">
            <div className="jo-xyz-demo-asset-item">
              <div className="jo-xyz-demo-asset-item__icon">🏢</div>
              <div className="jo-xyz-demo-asset-item__details">
                <h4>Downtown Office Building</h4>
                <p>Location: San Francisco, CA</p>
                <p>Value: $2,500,000</p>
              </div>
              <button
                className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
                onClick={() => handleAddAsset({
                  type: 'commercial-property',
                  name: 'Downtown Office Building',
                  location: 'San Francisco, CA',
                  value: 2500000,
                  source: 'netsuite'
                })}
              >
                Add Asset
              </button>
            </div>
          </div>
        </div>
      )}

      {formData.connectedSystems.some(s => s.id === 'quickbooks' && s.connected) && (
        <div className="jo-xyz-demo-connected-assets">
          <h3>Assets from QuickBooks</h3>
          <div className="jo-xyz-demo-asset-list">
            <div className="jo-xyz-demo-asset-item">
              <div className="jo-xyz-demo-asset-item__icon">🚛</div>
              <div className="jo-xyz-demo-asset-item__details">
                <h4>Fleet of Delivery Trucks</h4>
                <p>Location: Austin, TX</p>
                <p>Value: $450,000</p>
              </div>
              <button
                className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
                onClick={() => handleAddAsset({
                  type: 'trucks',
                  name: 'Fleet of Delivery Trucks',
                  location: 'Austin, TX',
                  value: 450000,
                  source: 'quickbooks'
                })}
              >
                Add Asset
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="jo-xyz-demo-manual-asset-form">
        <h3>Add Manual Asset</h3>
        <ManualAssetForm onSubmit={handleManualAsset} />
      </div>

      {formData.assets.length > 0 && (
        <div className="jo-xyz-demo-added-assets">
          <h3>Your Assets ({formData.assets.length})</h3>
          <div className="jo-xyz-demo-asset-list">
            {formData.assets.map((asset) => (
              <div key={asset.id} className="jo-xyz-demo-asset-item jo-xyz-demo-added">
                <div className="jo-xyz-demo-asset-item__icon">
                  {assetTypes.find(t => t.type === asset.type)?.icon || '📦'}
                </div>
                <div className="jo-xyz-demo-asset-item__details">
                  <h4>{asset.name}</h4>
                  <p>Value: ${asset.value.toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="jo-xyz-demo-step-actions">
        <button
          className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary"
          onClick={() => setCurrentStep(2)}
        >
          Back
        </button>
        <button
          className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
          onClick={handleComplete}
          disabled={formData.assets.length === 0}
        >
          Complete Onboarding
        </button>
      </div>
    </div>
  );

  return (
    <div className="jo-xyz-demo-onboard-borrower">
      <div className="jo-xyz-demo-onboard-borrower__header">
        <h1>Borrower Onboarding</h1>
        <div className="jo-xyz-demo-progress-steps">
          <div className={`jo-xyz-demo-step ${currentStep >= 1 ? 'jo-xyz-demo-active' : ''}`}>1. Connect Systems</div>
          <div className={`jo-xyz-demo-step ${currentStep >= 2 ? 'jo-xyz-demo-active' : ''}`}>2. Select Assets</div>
          <div className={`jo-xyz-demo-step ${currentStep >= 3 ? 'jo-xyz-demo-active' : ''}`}>3. Asset Details</div>
        </div>
      </div>

      <div className="jo-xyz-demo-onboard-borrower__content">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </div>
    </div>
  );
};

// Manual Asset Form Component
const ManualAssetForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    type: '',
    name: '',
    description: '',
    value: '',
    location: '',
    condition: 'excellent'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({
      type: '',
      name: '',
      description: '',
      value: '',
      location: '',
      condition: 'excellent'
    });
  };

  return (
    <form onSubmit={handleSubmit} className="jo-xyz-demo-manual-asset-form">
      <div className="jo-xyz-demo-form-group">
        <label>Asset Type</label>
        <select
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          required
        >
          <option value="">Select asset type</option>
          <option value="commercial-property">Commercial Property</option>
          <option value="trucks">Trucks & Vehicles</option>
          <option value="inventory">Inventory</option>
          <option value="equipment">Equipment</option>
        </select>
      </div>

      <div className="jo-xyz-demo-form-group">
        <label>Asset Name</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
      </div>

      <div className="jo-xyz-demo-form-group">
        <label>Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows="3"
        />
      </div>

      <div className="jo-xyz-demo-form-row">
        <div className="jo-xyz-demo-form-group">
          <label>Estimated Value ($)</label>
          <input
            type="number"
            value={formData.value}
            onChange={(e) => setFormData({ ...formData, value: e.target.value })}
            required
          />
        </div>

        <div className="jo-xyz-demo-form-group">
          <label>Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            required
          />
        </div>
      </div>

      <div className="jo-xyz-demo-form-group">
        <label>Condition</label>
        <select
          value={formData.condition}
          onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
        >
          <option value="excellent">Excellent</option>
          <option value="good">Good</option>
          <option value="fair">Fair</option>
          <option value="poor">Poor</option>
        </select>
      </div>

      <button type="submit" className="jo-xyz-demo-btn jo-xyz-demo-btn--primary">
        Add Asset
      </button>
    </form>
  );
};

export default OnboardBorrower;
